﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_STREAM_INVITATIONS_QUERY_KEY,
  LIVE_STREAM_LINK_BY_ID_QUERY_KEY,
  STREAM_INVITATION_API_URL
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options, StreamInvitationPayload } from '@/types';

export const liveStreamEndpoints = {
  getPublicLiveStreamLink: (streamEventId: number) =>
    axios.get(`${apiUrl()}${STREAM_INVITATION_API_URL}/publicStreamLink/${streamEventId}`),
  createStreamInvitation: (payload: StreamInvitationPayload) =>
    axios.post(`${apiUrl()}${STREAM_INVITATION_API_URL}`, payload),
  getAllStreamInvitations: (
    pageSize: number,
    cursor: number,
    streamEventId: number,
    isPublic?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${STREAM_INVITATION_API_URL}/all${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'filter.StreamEventId': streamEventId as number,
        'filter.IsPublic': isPublic as boolean
      })}`
    ),
  deleteStreamInvitation: (id: number) =>
    axios.delete(`${apiUrl()}${STREAM_INVITATION_API_URL}/${id}`),
  getGuestLiveStreamLink: (streamInvitationId: number) =>
    axios.get(`${apiUrl()}${STREAM_INVITATION_API_URL}/streamLink/${streamInvitationId}`)
};

export const useGetPublicLiveStreamLinkQuery = (
  streamEventId: number,
  enabled?: boolean,
  options?: Options
) => {
  return useQuery(
    [LIVE_STREAM_LINK_BY_ID_QUERY_KEY, { streamEventId }],
    () => liveStreamEndpoints.getPublicLiveStreamLink(streamEventId),
    {
      enabled: !!streamEventId && (enabled ?? true),
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useCreateStreamInvitationMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(liveStreamEndpoints.createStreamInvitation, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([ALL_STREAM_INVITATIONS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetAllStreamInvitations = (
  pageSize: number,
  streamEventId: number,
  isPublic?: boolean,
  enabled?: boolean
) => {
  return useInfiniteQuery(
    [ALL_STREAM_INVITATIONS_QUERY_KEY, { pageSize, streamEventId, isPublic }],
    ({ pageParam }) =>
      liveStreamEndpoints.getAllStreamInvitations(pageSize, pageParam, streamEventId, isPublic),
    {
      enabled: !!streamEventId && (enabled ?? true),
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      cacheTime: 1000
    }
  );
};

export const useDeleteStreamInvitationMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(liveStreamEndpoints.deleteStreamInvitation, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_STREAM_INVITATIONS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetGuestLiveStreamLinkQuery = (
  streamInvitationId: number,
  enabled?: boolean,
  options?: Options
) => {
  return useQuery(
    [LIVE_STREAM_LINK_BY_ID_QUERY_KEY, { streamInvitationId }],
    () => liveStreamEndpoints.getGuestLiveStreamLink(streamInvitationId),
    {
      enabled: !!streamInvitationId && (enabled ?? true),
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};
